"use client";
import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  Component,
} from "react";
import dynamic from "next/dynamic";
import styles from "./index.module.scss";
import { isApp } from "@/app/bridge";
import { shouldUseWebview } from "@/app/utils/url";
import {
  supportsStreamdown,
  supportsRegExpLookbehind,
} from "@/app/utils/device";
import TypewriterFallback from "./TypewriterFallback";

const LOG_PREFIX = "[AiAnswer]";

const LazyStreamdown = dynamic(
  () =>
    import("streamdown")
      .then((mod) => {
        console.info(LOG_PREFIX, "Streamdown module loaded successfully");
        return { default: mod.Streamdown };
      })
      .catch((err) => {
        console.error(
          LOG_PREFIX,
          "Streamdown module failed to load:",
          err?.message || err,
          "\nStack:",
          err?.stack,
        );
        throw err;
      }),
  {
    ssr: false,
    loading: () => null,
  },
);

// ── ErrorBoundary：捕获 Streamdown 渲染时的异常 ──
interface EBProps {
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback: React.ReactNode;
  children: React.ReactNode;
}
interface EBState {
  hasError: boolean;
}
class StreamdownErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { hasError: false };

  static getDerivedStateFromError(): EBState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

interface Props {
  content: string;
  streaming?: boolean;
  error?: boolean;
  onContentUpdate?: () => void;
  onTypingEnd?: () => void;
}

/**
 * AI回复组件 - 使用 Streamdown 实现流式 Markdown 渲染
 *
 * 状态说明：
 * - streaming=true: SSE 数据正在接收中，Streamdown 处于动画模式
 * - streaming=false: SSE 数据接收完毕
 * - error=true: 出错，立即静态显示全部内容
 *
 * 兼容性回退：
 * 当浏览器不支持 RegExp lookbehind（如 iOS 15.x Safari）时，
 * Streamdown 的依赖 remend 会在加载时抛出 SyntaxError，
 * 此时自动切换为 markdown-it + 打字机动画方案。
 */
export default function AiAnswerQuestion({
  content,
  streaming = true,
  error = false,
  onContentUpdate,
  onTypingEnd,
}: Props) {
  const onContentUpdateRef = useRef(onContentUpdate);
  const onTypingEndRef = useRef(onTypingEnd);
  const hasCalledTypingEndRef = useRef(false);
  const [useStreamdown, setUseStreamdown] = useState<boolean | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);

  useEffect(() => {
    const supported = supportsStreamdown();
    const lookbehind = supportsRegExpLookbehind();
    console.log("Compatibility check:", {
      supportsRegExpLookbehind: lookbehind,
      supportsStreamdown: supported,
      userAgent: navigator.userAgent,
    });
    setUseStreamdown(supported);
  }, []);

  const handleStreamdownRenderError = useCallback(
    (err: Error, errorInfo: React.ErrorInfo) => {
      console.error(
        LOG_PREFIX,
        "Streamdown render error, falling back to typewriter:",
        err?.message || err,
        "\nStack:",
        err?.stack,
        "\nComponent stack:",
        errorInfo?.componentStack,
      );
      setRenderFailed(true);
    },
    [],
  );

  useEffect(() => {
    onContentUpdateRef.current = onContentUpdate;
    onTypingEndRef.current = onTypingEnd;
  });

  useEffect(() => {
    if (!content) {
      hasCalledTypingEndRef.current = false;
    }
  }, [content]);

  const notifyTypingEnd = useCallback(() => {
    if (!hasCalledTypingEndRef.current && content) {
      hasCalledTypingEndRef.current = true;
      onTypingEndRef.current?.();
    }
  }, [content]);

  useEffect(() => {
    if (content) {
      onContentUpdateRef.current?.();
    }
  }, [content]);

  useEffect(() => {
    if (error && content) {
      notifyTypingEnd();
    }
  }, [error, content, notifyTypingEnd]);

  const handleAnimationEnd = useCallback(() => {
    notifyTypingEnd();
  }, [notifyTypingEnd]);

  const urlTransformFn = useCallback((url: string) => {
    if (isApp() && !shouldUseWebview(url)) {
      return `bambulab://bbl/webview?urlString=${encodeURIComponent(url)}`;
    }
    return url;
  }, []);

  const customComponents = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      a: ({ node, ...props }: any) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
      ),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      img: ({ node, ...props }: any) => <img {...props} />,
    }),
    [],
  );

  const allowedTags = useMemo(
    () => ({
      iframe: [
        "src",
        "style",
        "frameborder",
        "allowfullscreen",
        "width",
        "height",
        "scrolling",
        "allow",
        "title",
      ],
      div: ["style", "class"],
      span: ["style", "class"],
      img: ["src", "alt", "style", "width", "height", "loading"],
      table: ["style", "class"],
      td: ["style", "colspan", "rowspan"],
      th: ["style", "colspan", "rowspan"],
      tr: ["style"],
      thead: ["style"],
      tbody: ["style"],
      video: [
        "src",
        "style",
        "controls",
        "width",
        "height",
        "autoplay",
        "muted",
        "loop",
        "poster",
      ],
      source: ["src", "type"],
    }),
    [],
  );

  const processedContent = useMemo(() => {
    if (!content) return "";
    let result = content.replace(/<br\/>/g, "\n");
    result = result.replace(
      /!\[([^\]]*)\]\(([^)\s]+)(?:\s+=(\d*)x(\d*))?\s*\)/g,
      (match, alt, url, width, height) => {
        if (!width && !height) return match;
        let tag = `<img src="${url}" alt="${alt}"`;
        if (width) tag += ` width="${width}"`;
        if (height) tag += ` height="${height}"`;
        tag += ` />`;
        return tag;
      },
    );
    return result;
  }, [content]);

  const showLoading = !content && !error;

  if (showLoading) {
    return (
      <div className={styles.aiAnswerContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingDot}></div>
          <div className={styles.loadingDot}></div>
          <div className={styles.loadingDot}></div>
        </div>
      </div>
    );
  }

  if (useStreamdown === null) {
    return <div className={styles.aiAnswerContainer} />;
  }

  // 低版本浏览器或 Streamdown 渲染失败：使用打字机回退
  if (!useStreamdown || renderFailed) {
    return (
      <div className={styles.aiAnswerContainer}>
        <TypewriterFallback
          content={processedContent}
          streaming={streaming}
          error={error}
          onContentUpdate={onContentUpdate}
          onTypingEnd={onTypingEnd}
        />
      </div>
    );
  }

  return (
    <div className={styles.aiAnswerContainer}>
      <div className={styles.aiAnswerContent}>
        <StreamdownErrorBoundary
          onError={handleStreamdownRenderError}
          fallback={
            <TypewriterFallback
              content={processedContent}
              streaming={streaming}
              error={error}
              onContentUpdate={onContentUpdate}
              onTypingEnd={onTypingEnd}
            />
          }
        >
          <LazyStreamdown
            animated={!error}
            isAnimating={streaming && !error}
            components={customComponents}
            urlTransform={urlTransformFn}
            onAnimationEnd={handleAnimationEnd}
            allowedTags={allowedTags}
            normalizeHtmlIndentation
            controls={{
              table: { copy: false, download: false, fullscreen: false },
            }}
          >
            {processedContent}
          </LazyStreamdown>
        </StreamdownErrorBoundary>
      </div>
    </div>
  );
}
